/**
 * AlertHeaderMeta
 * Top row of the expanded panel: alert-type gradient icon circle,
 * type + level text, and the coloured status badge.
 */
import React from 'react';
import {
  FaTemperatureHigh,
  FaBan,
  LuCameraOff,
  MdOutlineSignalWifiStatusbarConnectedNoInternet4,
  FaLock,
  FaDoorClosed,
  PiBatteryLowFill,
  SiContactlesspayment,
  VscDebugDisconnect,
  HiStatusOffline,
  IoTimeOutline,
  TbAlertCircleFilled,
} from '@/components/atoms/Icons';
import { AlertLevelIcon } from '@/pages/Alert/AlertLevelIcon';
import { AlertLabels } from '@/services/alert/alerts.constants';
import PillCell from '@/components/organism/StatusPills/PillCell';
import { GradientDivider } from '@/components/atoms/GradientDivider';
import { GRADIENT } from '@/components/organism/TableExpand/primitives';
import { Text } from '@/components/atoms/AppText';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';

// ─── Type → Icon map ─────────────────────────────────────────────────────────
const ALERT_TYPE_ICON_MAP: Record<string, React.ElementType> = {
  TEMPERATURE: FaTemperatureHigh,
  PLANOGRAM_REJECTION: FaBan,
  CAMERA_OBSTRUCTION: LuCameraOff,
  CAMERA_BLOCKED: LuCameraOff,
  INTERNET_CONNECTIVITY: MdOutlineSignalWifiStatusbarConnectedNoInternet4,
  LOCK_ERROR: FaLock,
  DOOR_ERROR: FaDoorClosed,
  BATTERY_LOW: PiBatteryLowFill,
  PAYMENT_FAILURE: SiContactlesspayment,
  NO_PLANOGRAM: FaBan,
  EMPTY_PLANOGRAM: FaBan,
  WEAK_CONNECTIVITY: MdOutlineSignalWifiStatusbarConnectedNoInternet4,
  CCR_DISCONNECTED: VscDebugDisconnect,
  OFFLINE: HiStatusOffline,
  OPEN_TIMEOUT: IoTimeOutline,
};

const AlertTypeIcon = ({ type }: { type: string }) => {
  const Icon = ALERT_TYPE_ICON_MAP[type?.toUpperCase()] ?? TbAlertCircleFilled;
  return <Icon className="h-7 w-7 text-white" />;
};

// ─── Component ────────────────────────────────────────────────────────────────
export const AlertHeaderMeta = ({
  type,
  level,
  status,
  testId,
}: {
  type: string;
  level: string;
  status: string;
  testId?: string;
}) => {
  return (
    <div
      data-testid={testId ?? ''}
      className="relative flex items-center justify-between gap-4 flex-wrap px-4 py-3 rounded-xl overflow-hidden"
    >
      {/* Left accent bar */}
      <GradientDivider
        gradient={GRADIENT}
        side="left"
        className="absolute left-0 top-0 bottom-0 h-full"
      />

      {/* Left: icon + text */}
      <div className="flex items-center gap-3 pl-2">
        {/* Icon orb */}
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg"
          style={{ background: GRADIENT }}
        >
          <AlertTypeIcon type={type} />
        </div>

        {/* Type + level stacked */}
        <div className="flex flex-col leading-none gap-[3px]">
          <Text
            as="span"
            size="xs"
            weight="black"
            color="subtle"
            tracking="wider"
            uppercase
          >
            {AlertLabels.ALERT}
          </Text>
          <Text
            as="span"
            size="base"
            weight="extrabold"
            color="default"
            tracking="tight"
          >
            {type ? type.replace(/_/g, ' ') : '—'}
          </Text>
          <span className="flex items-center gap-1">
            <AlertLevelIcon
              level={level}
              className="w-3 h-3 text-gray-400 dark:text-gray-500"
            />
            <Text
              as="span"
              size="xs"
              weight="bold"
              color="subtle"
              tracking="widest"
              uppercase
            >
              {level || '—'}
            </Text>
          </span>
        </div>
      </div>

      {/* Right: status */}
      <div className="flex items-center gap-2">
        <Text
          as="span"
          size="xs"
          weight="black"
          color="subtle"
          tracking="wider"
          className="uppercase"
        >
          {ORGANISM_PROPS.ALERT_HEADER_META.STATUS_LABEL}
        </Text>
        <PillCell value={status} preset="alertStatus" dot />
      </div>
    </div>
  );
};
